import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { registerThisPhone } from '@/lib/native-push';
import { useNativeAppLinks } from '@/hooks/use-native-app-links';
import { AuthProvider, useAuth } from '@/hooks/use-auth';
import { useAuthStore } from '@/stores/authStore';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { HintLayer } from '@/components/HintLayer';
import { ConfirmDialogHost } from '@/components/ConfirmDialog';
import { AppLayout } from '@/components/layout/AppLayout';
import { SessionUnreachableScreen } from '@/components/layout/SessionUnreachable';
import { LoadingState } from '@/components/LoadingState';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/sonner';
import { AppUpdateBanner } from '@/components/AppUpdateBanner';
import { OfflineNotice } from '@/components/OfflineNotice';
import { buildLoginPath } from '@/lib/auth-redirect';
import { lazyPage } from '@/lib/lazy-page';
import { pageRoutes } from '@/page-routes';

// Pages load on demand (the signed-in ones are in page-routes.tsx).
const LoginPage = lazyPage(() => import('@/pages/login'));
const AcceptInvitePage = lazyPage(() => import('@/pages/accept-invite'));

/**
 * Every signed-in page: the session check, then the app shell around the page. Signed-out
 * visitors go to /login and come back here afterwards.
 */
function SignedInLayout() {
  const { user, loading, sessionUnreachable } = useAuth();
  const sessionExit = useAuthStore((state) => state.sessionExit);
  const location = useLocation();
  const userId = user?.id;

  // In the phone app, this phone gets the signed-in person's notifications (lib/native-push.ts).
  useEffect(() => {
    if (userId) void registerThisPhone();
  }, [userId]);

  // Signing out: the pages unmount before the session is cleared (utils/session-exit.ts).
  if (sessionExit) return <LoadingState fullPage label={sessionExit.label} />;
  // The factory didn't answer the session check: not signed out, so not /login.
  if (sessionUnreachable) return <SessionUnreachableScreen />;
  // Someone signed in on this device (this tab's session, or the last person remembered, T-68): the
  // app draws for them at once with what was remembered, and API calls wait for the session check.
  if (loading && !user) return <LoadingState fullPage label="Loading…" />;
  if (!user) {
    return <Navigate to={buildLoginPath(`${location.pathname}${location.search}${location.hash}`)} replace />;
  }

  // Keyed by the person: if the session check confirms someone other than the one remembered, every
  // page starts again for them, with its own loads (not only the shared loaders).
  return (
    <AppLayout key={user.id}>
      <Outlet />
    </AppLayout>
  );
}

/** Links and notifications the phone app opens go through the unsaved-changes guard (needs the router). */
function NativeAppLinks() {
  useNativeAppLinks();
  return null;
}

export default function App() {
  return (
    <TooltipProvider>
      <AuthProvider>
        <Router>
          {/* One hover-text layer for the whole app, above every layout: mark elements with data-hint. */}
          <HintLayer />
          <ConfirmDialogHost />
          <NativeAppLinks />
          <React.Suspense fallback={<LoadingState fullPage label="Loading…" />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/accept-invite/:token" element={<PublicLayout><AcceptInvitePage /></PublicLayout>} />

              <Route element={<SignedInLayout />}>
                {pageRoutes()}
              </Route>
            </Routes>
          </React.Suspense>
        </Router>
        <AppUpdateBanner />
        <OfflineNotice />
        <Toaster />
      </AuthProvider>
    </TooltipProvider>
  );
}
